import { Banner, Button, Typography } from '@neo4j-ndl/react';
import { getFlag } from '@nx/launch-darkly-service';
import type { IpFilter } from '@nx/state';
import { ACTION, consoleApi, useActiveOrg, useActiveProject, usePermissions } from '@nx/state';
import { BackButton, Page, PageTitle, PermissionTooltip } from '@nx/ui';
import { useState } from 'react';

import { CreateIpFilterSidebar } from './create-sidebar';
import { IpFilteringScopeProvider } from './ip-filter-scope';
import { IpFilteringTable } from './table';
import { IP_FILTERING_SCOPE } from './types';

interface IpFilteringProps {
  canCreate: boolean;
  ipFilters: IpFilter[];
  isLoading: boolean;
  scope: IP_FILTERING_SCOPE;
}

const IpFiltering = ({ canCreate, ipFilters, isLoading, scope }: IpFilteringProps) => {
  const [openCreate, setOpenCreate] = useState(false);

  const isUpxConsistencyEnabled = getFlag('upxConsistency', false);
  const PageContainer = isUpxConsistencyEnabled ? Page : 'div';
  const containerProps = isUpxConsistencyEnabled
    ? { contentType: 'light' as const }
    : { className: 'h-full overflow-auto p-4' };

  return (
    <IpFilteringScopeProvider scope={scope}>
      <PageContainer {...containerProps}>
        {!isUpxConsistencyEnabled && <BackButton to="../settings" />}

        {!isUpxConsistencyEnabled ? (
          <div className="mb-6 flex items-center justify-between px-2 pt-2">
            <Typography variant="h2">IP Filters</Typography>
            <PermissionTooltip hasButtonWrapper hasPermission={canCreate}>
              <Button
                isDisabled={!canCreate}
                onClick={() => setOpenCreate(true)}
                htmlAttributes={{
                  'data-testid': 'create-ip-filter-btn',
                }}
              >
                Create IP Filter
              </Button>
            </PermissionTooltip>
          </div>
        ) : (
          <PageTitle
            title="IP Filters"
            back={{
              label: 'Back to security',
              toUrl: '../security',
            }}
            trailingContent={
              <PermissionTooltip hasButtonWrapper hasPermission={canCreate}>
                <Button
                  isDisabled={!canCreate}
                  onClick={() => setOpenCreate(true)}
                  htmlAttributes={{
                    'data-testid': 'create-ip-filter-btn',
                  }}
                >
                  Create IP Filter
                </Button>
              </PermissionTooltip>
            }
          />
        )}
        <div>
          {scope === IP_FILTERING_SCOPE.PROJECT && (
            <Banner type="info" className="m-4 items-center" hasIcon usage="inline">
              <Typography variant="body-medium">
                Read-only View: Organization Admins can Create and Edit IP Filter rules from the organisation settings
                menu.
              </Typography>
            </Banner>
          )}
          <IpFilteringTable ipFilters={ipFilters} isLoading={isLoading} />
        </div>
      </PageContainer>
      {openCreate && <CreateIpFilterSidebar onClose={() => setOpenCreate(false)} canCreate={canCreate} />}
    </IpFilteringScopeProvider>
  );
};

export const OrganizationIpFiltering = () => {
  const { isAllowed } = usePermissions();

  const organization = useActiveOrg();

  const isAllowedToCreateIpFilters = isAllowed(ACTION.CREATE, `organizations/${organization.id}/ip-filters`);
  const canCreateIpFilters = isAllowedToCreateIpFilters && organization.capabilities.ip_filtering;

  const { data: ipFilters = [], isLoading } = consoleApi.useListIpFiltersByOrganizationQuery(organization.id, {
    skip: !organization.capabilities.ip_filtering,
    pollingInterval: 10000,
  });

  return (
    <IpFiltering
      canCreate={canCreateIpFilters}
      ipFilters={ipFilters}
      isLoading={isLoading}
      scope={IP_FILTERING_SCOPE.ORGANIZATION}
    />
  );
};

export const NamespaceIpFiltering = () => {
  const organization = useActiveOrg();
  const project = useActiveProject();

  const { data: ipFilters = [], isLoading } = consoleApi.useListIpFiltersByProjectQuery(project.id, {
    skip: !organization.capabilities.ip_filtering,
    pollingInterval: 10000,
  });

  return (
    <IpFiltering canCreate={false} ipFilters={ipFilters} isLoading={isLoading} scope={IP_FILTERING_SCOPE.PROJECT} />
  );
};
